import { createStore, type Store, useStore } from "@tanstack/react-store";
import { createContext, type PropsWithChildren, use, useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import z from "zod";
import { api } from "@/api/api";
import { getUserProfile, loginByCode, refreshToken } from "@/api/services";
import type { UserProfile } from "@/api/types";
import { Button } from "@/components/ui/button";
import { collabKeys } from "@/lib/collab-keys";
import { LocalStorage } from "@/lib/local-storage";
import { reloginUrl } from "@/lib/login-attempts";
import { rollbar } from "@/lib/rollbar";

type InitializationState = "init" | "ready" | "logging-out";

export interface AuthContextType {
  userStore: Store<UserProfile | null>;
  initializationStore: Store<InitializationState>;
  logout: () => void;
  login: (code: string) => Promise<void>;
  initializationEffect: () => Promise<void>;
}

const tokensSchema = z.object({
  accessToken: z.string(),
  accessTokenExpiry: z.string(),
  refreshToken: z.string(),
  refreshTokenExpiry: z.string(),
});

type Tokens = z.infer<typeof tokensSchema>;

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [tokens, _setTokens] = useState<Tokens | null>(() =>
    LocalStorage.get(LocalStorage.TOKENS_KEY, tokensSchema),
  );

  const interceptorsRef = useRef<{
    tokensInterceptor: number | null;
    unauthorizedInterceptor: number | null;
  }>({
    tokensInterceptor: null,
    unauthorizedInterceptor: null,
  });

  const [initializationStore] = useState(() => createStore("init" as InitializationState));
  const initialization = useStore(initializationStore, (state) => state);

  const [userStore] = useState(() => createStore<UserProfile | null>(null));

  const setTokens = useCallback((newTokens: Tokens, expiry: number) => {
    LocalStorage.set(LocalStorage.TOKENS_KEY, newTokens, expiry);
    _setTokens(newTokens);
  }, []);

  const loginSetup = useCallback(
    (tokens: Tokens, user: UserProfile) => {
      setTokens(tokens, new Date(tokens.refreshTokenExpiry).getTime() - Date.now());
      userStore.setState(() => user);
      rollbar.configure({
        payload: { person: { id: user.id.toString(), username: user.username, email: user.email } },
      });

      if (interceptorsRef.current.tokensInterceptor !== null) {
        api.interceptors.request.eject(interceptorsRef.current.tokensInterceptor);
      }
      if (interceptorsRef.current.unauthorizedInterceptor !== null) {
        api.interceptors.response.eject(interceptorsRef.current.unauthorizedInterceptor);
      }

      interceptorsRef.current.tokensInterceptor = api.interceptors.request.use((config) => {
        config.headers.set("Authorization", `Bearer ${tokens.accessToken}`);
        return config;
      });
      interceptorsRef.current.unauthorizedInterceptor = api.interceptors.response.use(
        (response) => response,
        async (error) => {
          if (error.response?.status === 401) {
            toast.warning("Session expired. Refresh to re-authenticate.", {
              action: (
                <Button size="sm" className="ml-auto" onClick={() => window.location.reload()}>
                  Refresh
                </Button>
              ),
            });
          }
          return Promise.reject(error);
        },
      );
    },
    [setTokens, userStore],
  );

  const logout = useCallback(
    (relogin?: boolean) => {
      initializationStore.setState(() => "logging-out");
      LocalStorage.remove(LocalStorage.TOKENS_KEY);
      // Collab keys belong to the signed-in user: drop them with the session.
      collabKeys.clear();
      window.open(reloginUrl(relogin), "_self");
    },
    [initializationStore],
  );

  useEffect(() => {
    if (initialization !== "ready" || !tokens) return;

    // Setup token refresh interval
    const refreshInterval = setInterval(
      async () => {
        if (!tokens) return;

        const now = Date.now();
        // refresh the token if it's going to expire in the next 2 minutes
        const accessTokenExpiry = new Date(tokens.accessTokenExpiry).getTime();
        if (accessTokenExpiry - now < 2 * 60 * 1000) {
          const response = await refreshToken(tokens.refreshToken).catch(() => {
            // failed to refresh token, probably invalid refresh token
            return null;
          });

          if (response) {
            loginSetup(
              {
                accessToken: response.token.access_token,
                refreshToken: response.token.refresh_token,
                accessTokenExpiry: response.token.access_valid_until,
                refreshTokenExpiry: response.token.refresh_valid_until,
              },
              response.user,
            );
          } else {
            logout(true);
          }
        }
      },
      1 * 60 * 1000,
    ); // every 1 minute

    return () => {
      clearInterval(refreshInterval);
    };
  }, [initialization, tokens, loginSetup, logout]);

  const initializationEffect = useCallback(async () => {
    if (initializationStore.state !== "init") return;

    // give it a minute buffer before expiry
    const aTokenExpiry = tokens
      ? new Date(tokens.accessTokenExpiry).getTime() - 1 * 60 * 1000 - Date.now()
      : -1;
    const rTokenExpiry = tokens
      ? new Date(tokens.refreshTokenExpiry).getTime() - 1 * 60 * 1000 - Date.now()
      : -1;
    if (tokens && (aTokenExpiry > 1 || rTokenExpiry > 1)) {
      let response = null;
      if (aTokenExpiry > 0) {
        response = await getUserProfile(tokens.accessToken).catch(() => {
          return null;
        });
      }
      if (!response && rTokenExpiry > 0) {
        // access token probably expired, try refreshing
        response = await refreshToken(tokens.refreshToken).catch(() => {
          return null;
        });
      }

      if (response) {
        loginSetup(
          {
            accessToken: response.token?.access_token || tokens.accessToken,
            refreshToken: response.token?.refresh_token || tokens.refreshToken,
            accessTokenExpiry: response.token?.access_valid_until || tokens.accessTokenExpiry,
            refreshTokenExpiry: response.token?.refresh_valid_until || tokens.refreshTokenExpiry,
          },
          response.user,
        );
      }

      // ignore else, user remains null it would be treated as unauthenticated
    }

    initializationStore.setState(() => "ready");
  }, [tokens, loginSetup, initializationStore]);

  const login = useCallback(
    async (code: string) => {
      const resp = await loginByCode(code);

      loginSetup(
        {
          accessToken: resp.token.access_token,
          refreshToken: resp.token.refresh_token,
          accessTokenExpiry: resp.token.access_valid_until,
          refreshTokenExpiry: resp.token.refresh_valid_until,
        },
        resp.user,
      );
      initializationStore.setState(() => "ready");
    },
    [loginSetup, initializationStore],
  );

  return (
    <AuthContext.Provider
      value={{
        initializationStore,
        userStore,
        logout,
        login,
        initializationEffect,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = use(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

export const useUser = () => {
  const { userStore } = useAuth();
  const user = useStore(userStore, (state) => state);

  if (!user) {
    throw new Error("useUser must be used when user is authenticated");
  }

  return user;
};

/**
 * Whether the current user has the given Wagtail role slug (OBS-1415),
 * e.g. `useHasRole(Role.PICTURE_EDITOR)`. Use to gate role-restricted UI.
 */
export const useHasRole = (role: string) => {
  const { roles } = useUser();
  return roles.includes(role);
};
